import { useState } from 'react';
import { text } from '../../app/i18n';
import { TemplateForm, type TemplateSubmission } from '../../work/TemplateForm';
import type { CustomEditor, EditorConfig, PreviewFormat, PropertyGroup } from '../editor/editor-types';
import { getAt, type Pointer } from '../editor/json-pointer';
import { describeProperties, type SchemaNode, type SchemaRegistry } from '../editor/schema-properties';
import { boundEditor, computeEditor, conditionEditor, enumValuesEditor, ruleRefEditor, selectorFilterEditor, type ConformityRuleOption } from './forms-editors';
import { blockKindAt, classifyTemplate, localFindings, nodeOfPath, scopeOf, templateTree, TYPE_LABELS, type Template } from './forms-model';

/**
 * Configuration du socle d'éditeur pour les gabarits `inspection_template` (ADR-141 §2) : schéma du contrat
 * `inspection-template-body`, ordre et libellés des propriétés, éditeurs dédiés, renderer d'aperçu
 * (`TemplateForm`, le même que l'écran de tâche), formats de l'application opérateur.
 */

export const TEMPLATE_SCHEMA_ID = 'inspection-template-body.schema.json';

/** Appareils de l'aperçu : tablette et mobile en portrait ou en paysage (segment « Orientation »), le web suit l'écran de tâche. */
export const FORM_PREVIEW_FORMATS: readonly PreviewFormat[] = [
  { id: 'tablet', label: 'Tablette', width: 1280, height: 800, orientation: 'landscape' },
  { id: 'mobile', label: 'Mobile', width: 390, height: 844, orientation: 'portrait' },
  { id: 'web', label: 'Web', width: 1280, height: 800 },
];

const LABELS: Readonly<Record<string, string>> = {
  label: 'Libellé',
  description: 'Description',
  section_id: 'Identifiant de la section',
  visible_when: 'Visible quand',
  required_when: 'Obligatoire aussi quand',
  series: 'Série ouverte',
  'series.min_count': 'Lignes au moins',
  'series.max_count': 'Lignes au plus',
  'series.exit': 'Décision de sortie',
  'series.exit.when': 'Sortie possible quand (sur la ligne)',
  'series.exit.required': 'Une ligne de sortie est exigée',
  'series.exit.prompt': 'Question de sortie',
  repeat: 'Répétée par élément',
  field: 'Champ',
  'field.field_id': 'Identifiant du champ',
  'field.type': 'Type',
  'field.required': 'Obligatoire',
  'field.unit': 'Unité',
  'field.reference_type': 'Type d’objet à choisir',
  'field.validation': 'Bornes fixes',
  'field.validation.min': 'Minimum',
  'field.validation.max': 'Maximum',
  presentation: 'Présentation (affichage seulement)',
  'presentation.widget': 'Composant de saisie',
  'presentation.layout': 'Mise en page',
  'presentation.layout.span': 'Largeur',
  'presentation.selector': 'Sélecteur',
  'presentation.selector.label_field': 'Champ affiché',
  'presentation.selector.code_field': 'Code affiché',
  'presentation.selector.search': 'Recherche',
  'presentation.selector.filter': 'Filtre',
  target: 'Cible',
  'target.rule_ref': 'Règle de conformité partagée',
  'target.min': 'Minimum',
  'target.max': 'Maximum',
  'target.unit': 'Unité',
  'target.on_out_of_target': 'Hors cible',
  compute: 'Calcul',
  nonconformity_when: 'Non conforme quand',
  evidence: 'Preuve',
  'evidence.min_count': 'Nombre de preuves (0 = facultative)',
  'evidence.kind': 'Nature',
  'evidence.when': 'Demandée quand',
};

const ENUM_LABELS: Readonly<Record<string, Readonly<Record<string, string>>>> = {
  'field.type': TYPE_LABELS,
  'presentation.widget': {
    keypad: 'Pavé numérique',
    slider: 'Curseur',
    radio_tiles: 'Tuiles',
    toggle: 'Interrupteur',
    signature: 'Signature',
    scan_code: 'Scan de code',
    scan_doc: 'Scan de document',
    photo: 'Photo',
    table: 'Tableau',
    html: 'Texte d’aide',
    selector: 'Sélecteur',
    stepper: 'Plus / moins',
    date: 'Date',
    datetime: 'Date et heure',
    time: 'Heure',
    stars: 'Étoiles',
    gallery: 'Galerie',
    lot: 'Lot',
    calc: 'Total calculé',
  },
  'presentation.layout.span': { auto: 'Automatique', full: 'Pleine largeur' },
  'target.on_out_of_target': { nc_prompt: 'Demander « Ouvrir une non-conformité ? »', nc_required: 'Non-conformité obligatoire', wait: 'Pas encore (jamais une non-conformité)' },
  'evidence.kind': { photo: 'Photo', signature: 'Signature', document: 'Document' },
};

const EVENTS_RESERVED = [
  {
    id: 'events',
    label: 'Événements',
    note: 'Les événements du formulaire (à la saisie d’un champ, à la validation) prendront place ici, dans la grammaire du manifeste de composant (ADR-144, proposé). Rien n’est encore éditable.',
  },
];

const isRecord = (value: unknown): value is Record<string, unknown> => value !== null && typeof value === 'object' && !Array.isArray(value);

/** Schémas d'une section et d'une entrée de champ, lus dans le contrat. */
function parts(registry: SchemaRegistry): { template: SchemaNode; section: SchemaNode; entry: SchemaNode } {
  const template = registry[TEMPLATE_SCHEMA_ID];
  if (template === undefined) throw new Error(`Contrat ${TEMPLATE_SCHEMA_ID} absent du registre.`);
  const sections = (template['properties'] as Record<string, SchemaNode>)['sections']!;
  const section = sections['items'] as SchemaNode;
  const fields = (section['properties'] as Record<string, SchemaNode>)['fields']!;
  return { template, section, entry: fields['items'] as SchemaNode };
}

/** Panneau de propriétés d'un bloc du gabarit, généré du contrat. */
export function templateProperties(registry: SchemaRegistry, rules: readonly ConformityRuleOption[], template: Template, pointer: Pointer): PropertyGroup<Template> | null {
  const kind = blockKindAt(pointer);
  if (kind === null) return null;
  const schema = parts(registry);
  const scope = scopeOf(template, pointer);
  const condition = conditionEditor(scope, 'Toujours');
  if (kind === 'template') {
    return { title: 'Formulaire', base: '', descriptors: describeProperties(schema.template, registry, { include: { '': ['label', 'description'] } }), labels: LABELS, reserved: EVENTS_RESERVED };
  }
  if (kind === 'section') {
    const section = getAt(template, pointer);
    const include = isRecord(section) && isRecord(section['repeat']) ? ['section_id', 'label', 'visible_when'] : ['section_id', 'label', 'visible_when', 'series'];
    const exitScope = isRecord(section) && Array.isArray(section['fields']) ? scopeOf(template, `${pointer}/fields/0`) : scope;
    return {
      title: 'Section',
      base: pointer,
      descriptors: describeProperties(schema.section, registry, { include: { '': include, series: ['min_count', 'max_count', 'exit'], 'series.exit': ['prompt', 'required', 'when'] } }, '', schema.template),
      labels: LABELS,
      editors: { visible_when: condition, 'series.exit.when': conditionEditor(exitScope, 'Jamais') },
      scope,
    };
  }
  const entry = getAt(template, pointer);
  const field = isRecord(entry) && isRecord(entry['field']) ? entry['field'] : {};
  const type = String(field['type'] ?? '');
  const fieldKeys = ['field_id', 'label', 'description', 'type', 'required', ...(['number', 'decimal', 'quantity', 'money'].includes(type) ? ['unit', 'validation'] : []), ...(type === 'reference' ? ['reference_type'] : []), ...(type === 'enumeration' ? ['validation'] : [])];
  const editors: Record<string, CustomEditor<Template>> = {
    visible_when: condition,
    required_when: conditionEditor(scope, 'Jamais'),
    nonconformity_when: conditionEditor(scope, 'Jamais'),
    'evidence.when': condition,
    'target.min': boundEditor,
    'target.max': boundEditor,
    'target.rule_ref': ruleRefEditor(rules, scope),
    compute: computeEditor,
    'presentation.selector.filter': selectorFilterEditor,
  };
  if (type === 'enumeration') editors['field.validation'] = enumValuesEditor;
  const measure = ['number', 'decimal', 'quantity'].includes(type);
  const entryKeys = ['field', 'presentation', 'required_when', 'visible_when', ...(measure ? ['target', 'compute'] : []), ...(type === 'enumeration' || type === 'boolean' ? ['nonconformity_when'] : []), 'evidence'];
  return {
    title: `Champ — ${text(field['label'] as Record<string, string> | undefined, 'fr', String(field['field_id'] ?? ''))}`,
    base: pointer,
    descriptors: describeProperties(schema.entry, registry, {
      include: {
        '': entryKeys,
        field: fieldKeys,
        'field.validation': ['min', 'max'],
        presentation: type === 'reference' ? ['widget', 'selector', 'layout'] : ['widget', 'layout'],
        'presentation.selector': ['label_field', 'code_field', 'search', 'filter'],
        target: ['rule_ref', 'min', 'max', 'unit', 'on_out_of_target'],
        evidence: ['min_count', 'kind', 'when'],
      },
    }, '', schema.template),
    labels: LABELS,
    enumLabels: ENUM_LABELS,
    editors,
    scope,
    reserved: EVENTS_RESERVED,
  };
}

/**
 * Entrées d'exemple d'un aperçu (branchement du lot F2, arbitrage du 2026-10-18) : une section répétée reçoit sa liste
 * d'éléments du workflow ; hors d'une tâche, l'éditeur en fabrique deux, jamais envoyées ni enregistrées.
 */
export function sampleInputs(template: Template): Record<string, string[]> {
  const inputs: Record<string, string[]> = {};
  for (const section of template.sections) {
    if (section.repeat !== undefined) inputs[section.repeat.items_input] = ['ffffffffffffffffffff0001', 'ffffffffffffffffffff0002'];
  }
  return inputs;
}

/** Aperçu en direct : le rendu de l'écran de tâche, rempli pour essayer, sans aucune commande. */
function TemplatePreview({ template }: { template: Template }) {
  const [submitted, setSubmitted] = useState<TemplateSubmission | null>(null);
  return (
    <div className="ay-forms-preview">
      <TemplateForm template={template} subject={undefined} disabled={false} errors={{}} onSubmit={setSubmitted} inputs={sampleInputs(template)} preview />
      {submitted !== null && <p className="ay-forms-preview__result" role="status">Essai validé : {Object.keys(submitted.answers ?? {}).length} réponse(s). Rien n’est envoyé.</p>}
    </div>
  );
}

export function formsEditorConfig(registry: SchemaRegistry, rules: readonly ConformityRuleOption[]): EditorConfig<Template> {
  return {
    noun: 'Formulaire',
    tree: templateTree,
    properties: (template, pointer) => templateProperties(registry, rules, template, pointer),
    localFindings,
    classify: classifyTemplate,
    nodeOf: nodeOfPath,
    renderPreview: (template) => <TemplatePreview template={template} />,
    formats: FORM_PREVIEW_FORMATS,
  };
}
